import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReportFilters, CompareMode } from '../../types/Report';
import { productService, Product } from '../../services/productService';
import { reportService } from '../../services/reportService';

interface FilterBarProps {
  filters: ReportFilters;
  onFiltersChange: (filters: ReportFilters) => void;
  onRun: () => void;
  onReset: () => void;
  loading?: boolean;
  segmentType?: string; // URL segment type (e.g., 'customer', 'product', 'salesagent')
  segmentId?: string | number; // URL segment ID
  segmentName?: string; // Display name for the segment
}

type FilterType =
  | 'all'
  | 'category'
  | 'brand'
  | 'product'
  | 'customer'
  | 'sales agent'
  | 'territory';

interface FilterOption {
  id: string | number;
  name: string;
  code?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFiltersChange,
  onRun: _onRun,
  onReset,
  loading = false,
  segmentType,
  segmentId,
  segmentName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [localFilters, setLocalFilters] = useState<ReportFilters>(filters);

  // Map URL segment types to FilterBar filter types
  const getFilterTypeFromSegment = (urlType: string | undefined): FilterType => {
    if (!urlType) return 'all';
    const typeMap: { [key: string]: FilterType } = {
      customer: 'customer',
      product: 'product',
      salesagent: 'sales agent',
      territory: 'territory',
      category: 'category',
      brand: 'brand',
    };
    return typeMap[urlType.toLowerCase()] || 'all';
  };

  const [filterType, setFilterType] = useState<FilterType>(() => {
    // Check if we're on a segmented page from URL or props
    const segmentedPageMatch = location.pathname.match(
      /^\/reports\/executive-dashboard\/([^/]+)\/([^/]+)$/
    );
    if (segmentedPageMatch) {
      const [, urlType] = segmentedPageMatch;
      return getFilterTypeFromSegment(urlType);
    }
    return segmentType ? getFilterTypeFromSegment(segmentType) : 'all';
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<FilterOption[]>([]);
  const [selectedFilterItem, setSelectedFilterItem] = useState<FilterOption | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Initialize filter type and selected item when on a segmented page
  useEffect(() => {
    const segmentedPageMatch = location.pathname.match(
      /^\/reports\/executive-dashboard\/([^/]+)\/([^/]+)$/
    );

    if (segmentedPageMatch) {
      const [, urlType, urlId] = segmentedPageMatch;
      const mappedFilterType = getFilterTypeFromSegment(urlType);
      setFilterType(mappedFilterType);

      // Set selected item if we have segment info
      if (segmentId || urlId) {
        const itemId = segmentId || urlId;
        const itemName: string = segmentName || String(itemId);
        setSelectedFilterItem({
          id: itemId,
          name: itemName,
        });
        setSearchTerm(itemName);
      }
    } else if (segmentType && segmentId) {
      // Use props if provided (for cases where URL might not match pattern)
      const mappedFilterType = getFilterTypeFromSegment(segmentType);
      setFilterType(mappedFilterType);
      const displayName = segmentName || String(segmentId);
      setSelectedFilterItem({
        id: segmentId,
        name: displayName,
      });
      setSearchTerm(displayName);
    }
  }, [location.pathname, segmentType, segmentId, segmentName]);

  // Update selected item name when segmentName becomes available (e.g., after data loads)
  useEffect(() => {
    if (
      segmentName &&
      selectedFilterItem &&
      selectedFilterItem.id === segmentId &&
      selectedFilterItem.name !== segmentName
    ) {
      setSelectedFilterItem({
        ...selectedFilterItem,
        name: segmentName,
      });
      setSearchTerm(segmentName);
    }
  }, [segmentName, segmentId]); // Removed selectedFilterItem from dependencies to prevent infinite loop

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Perform search when debounced term changes
  useEffect(() => {
    if (filterType === 'all' || !debouncedSearchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const performSearch = async () => {
      setLoadingSearch(true);
      try {
        let results: FilterOption[] = [];

        switch (filterType) {
          case 'product': {
            const productResponse = await productService.searchProductsPaginated(
              debouncedSearchTerm,
              1,
              50,
              false,
              true // includeAll: true to show all products
            );
            // Use product_code as id (same as widgets use)
            results = productResponse.products.map((p: Product) => ({
              id: p.product_code, // Use product_code, not database ID
              name: p.name,
              code: p.product_code,
            }));
            break;
          }

          case 'brand': {
            // Use brands from products table (same as widgets use) instead of brands table
            const brandStrings = await reportService.searchBrandsFromProducts(debouncedSearchTerm);
            // Use brand string as id (same as widgets use - p.brand from products table)
            results = brandStrings.map((brand: string) => ({
              id: brand, // Use brand string from products table, not brand_code from brands table
              name: brand,
            }));
            break;
          }

          case 'category': {
            const categories = await reportService.searchCategories(debouncedSearchTerm);
            // Use category string as id (same as widgets use)
            results = categories.map((cat: string) => ({
              id: cat, // Category string is the identifier
              name: cat,
            }));
            break;
          }

          case 'customer': {
            const customers = await reportService.searchCustomers(debouncedSearchTerm);
            // Use CustomerNumber as id (same as widgets use)
            results = customers.map((cust: { id: string; name: string }) => ({
              id: cust.id, // CustomerNumber is already the identifier
              name: cust.name,
            }));
            break;
          }

          case 'sales agent': {
            const agents = await reportService.searchSalesAgents(debouncedSearchTerm);
            // Use SalesPersonID as id (same as widgets use)
            results = agents.map((agent: string) => ({
              id: agent, // SalesPersonID is already the identifier
              name: agent,
            }));
            break;
          }

          case 'territory': {
            const territories = await reportService.searchTerritories(debouncedSearchTerm);
            // Use SalesTerritory as id (same as widgets use)
            results = territories.map((terr: string) => ({
              id: terr, // SalesTerritory is already the identifier
              name: terr,
            }));
            break;
          }
        }

        setSearchResults(results);
      } catch (error) {
        console.error('Error searching:', error);
        setSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm, filterType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateChange = useCallback((field: 'startDate' | 'endDate', value: string) => {
    setLocalFilters((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
  }, []);

  const handleCompareModeChange = useCallback((mode: CompareMode) => {
    setLocalFilters((prev) => {
      const updated = { ...prev, compareMode: mode };
      return updated;
    });
  }, []);

  const handleFilterTypeChange = useCallback((type: FilterType) => {
    setFilterType(type);
    setSearchTerm('');
    setSelectedFilterItem(null);
    setSearchResults([]);
    if (type === 'all') {
      // Clear filter-related fields
      setLocalFilters((prev) => {
        const updated = { ...prev };
        delete updated.productIds;
        delete updated.categoryIds;
        delete updated.brandIds;
        delete updated.customerIds;
        delete updated.territoryIds;
        delete updated.salesPersonIds;
        return updated;
      });
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
    if (!value.trim()) {
      setSelectedFilterItem(null);
    }
  }, []);

  const handleSelectFilterItem = useCallback(
    (item: FilterOption) => {
      setSelectedFilterItem(item);
      setSearchTerm(item.code ? `${item.name} (${item.code})` : item.name);
      setShowDropdown(false);

      // Update filters based on filter type
      setLocalFilters((prev) => {
        const updated = { ...prev };
        // Clear all filter IDs first
        delete updated.productIds;
        delete updated.categoryIds;
        delete updated.brandIds;
        delete updated.customerIds;
        delete updated.territoryIds;
        delete updated.salesPersonIds;

        // Set the appropriate filter ID (using the same identifiers as widgets)
        switch (filterType) {
          case 'product':
            // item.id is product_code
            updated.productIds = [item.id as any]; // Backend expects product codes in productIds
            break;
          case 'category':
            // item.id is category string
            updated.categoryIds = [item.id as string];
            break;
          case 'brand':
            // item.id is brand_code
            updated.brandIds = [item.id as string];
            break;
          case 'customer':
            // item.id is CustomerNumber
            updated.customerIds = [item.id as string];
            break;
          case 'sales agent':
            // item.id is SalesPersonID
            updated.salesPersonIds = [item.id as string];
            break;
          case 'territory':
            // item.id is SalesTerritory
            updated.territoryIds = [item.id as string];
            break;
        }
        return updated;
      });
    },
    [filterType]
  );

  const handleRun = useCallback(() => {
    // Apply local filters
    onFiltersChange(localFilters);

    // Build query params
    const params = new URLSearchParams();
    if (localFilters.startDate) params.set('startDate', localFilters.startDate);
    if (localFilters.endDate) params.set('endDate', localFilters.endDate);
    if (localFilters.compareMode) params.set('compareMode', localFilters.compareMode);

    // Add filter IDs to query params if they exist
    if (localFilters.customerIds && localFilters.customerIds.length > 0) {
      params.set('customerIds', localFilters.customerIds.join(','));
    }
    if (localFilters.productIds && localFilters.productIds.length > 0) {
      params.set('productIds', localFilters.productIds.map(String).join(','));
    }
    if (localFilters.categoryIds && localFilters.categoryIds.length > 0) {
      params.set('categoryIds', localFilters.categoryIds.join(','));
    }
    if (localFilters.brandIds && localFilters.brandIds.length > 0) {
      params.set('brandIds', localFilters.brandIds.join(','));
    }
    if (localFilters.territoryIds && localFilters.territoryIds.length > 0) {
      params.set('territoryIds', localFilters.territoryIds.join(','));
    }
    if (localFilters.salesPersonIds && localFilters.salesPersonIds.length > 0) {
      params.set('salesPersonIds', localFilters.salesPersonIds.join(','));
    }

    // Check if we're on a segmented page (pattern: /reports/executive-dashboard/{type}/{id})
    const segmentedPageMatch = location.pathname.match(
      /^\/reports\/executive-dashboard\/([^/]+)\/([^/]+)$/
    );

    // If a filter is selected, navigate to the segmented page URL format
    // This applies whether we're on the main dashboard or already on a segmented page
    if (filterType !== 'all' && selectedFilterItem) {
      // Map filter type to URL type
      const urlTypeMap: { [key: string]: string } = {
        product: 'product',
        category: 'category',
        brand: 'brand',
        customer: 'customer',
        'sales agent': 'salesagent',
        territory: 'territory',
      };
      const urlType = urlTypeMap[filterType];
      if (urlType) {
        // Trim product codes to remove trailing spaces
        const urlItemId =
          urlType === 'product' && typeof selectedFilterItem.id === 'string'
            ? selectedFilterItem.id.trim()
            : selectedFilterItem.id;
        // Encode the item ID for URL (handles spaces and special characters)
        const encodedItemId = encodeURIComponent(String(urlItemId));
        navigate(`/reports/executive-dashboard/${urlType}/${encodedItemId}?${params.toString()}`);
        return;
      }
    }

    // If we're on a segmented page and no new filter is selected, preserve the segment
    if (segmentedPageMatch) {
      const [, segmentType, segmentId] = segmentedPageMatch;
      navigate(`/reports/executive-dashboard/${segmentType}/${segmentId}?${params.toString()}`);
      return;
    }

    // Otherwise, navigate to executive dashboard with query params (no filter selected)
    navigate(`/reports/executive-dashboard?${params.toString()}`);
  }, [localFilters, filterType, selectedFilterItem, onFiltersChange, navigate, location.pathname]);

  const handleReset = useCallback(() => {
    const defaultFilters: ReportFilters = {
      compareMode: 'yoy',
    };
    // Set default date range to year-to-date (first day of current year to today)
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    defaultFilters.startDate = startDate.toISOString().split('T')[0];
    defaultFilters.endDate = endDate.toISOString().split('T')[0];

    setLocalFilters(defaultFilters);
    setFilterType('all');
    setSearchTerm('');
    setSelectedFilterItem(null);
    setSearchResults([]);
    onFiltersChange(defaultFilters);
    onReset();
  }, [onFiltersChange, onReset]);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Start Date
          </label>
          <input
            type="date"
            value={localFilters.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            End Date
          </label>
          <input
            type="date"
            value={localFilters.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Compare Mode
          </label>
          <select
            value={localFilters.compareMode || 'yoy'}
            onChange={(e) => handleCompareModeChange(e.target.value as CompareMode)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px',
            }}
          >
            <option value="yoy">Year-over-Year</option>
            <option value="previous">Previous Period</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Filter
          </label>
          <select
            value={filterType}
            onChange={(e) => handleFilterTypeChange(e.target.value as FilterType)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '150px',
            }}
          >
            <option value="all">All</option>
            <option value="category">Category</option>
            <option value="brand">Brand</option>
            <option value="product">Product</option>
            <option value="customer">Customer</option>
            <option value="sales agent">Sales Agent</option>
            <option value="territory">Territory</option>
          </select>
        </div>

        {filterType !== 'all' && (
          <div style={{ position: 'relative' }} ref={searchRef}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Search {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                placeholder={`Search by ${filterType} name${filterType === 'product' ? ', code, or ID' : ''}...`}
                style={{
                  padding: '0.5rem',
                  paddingRight: loadingSearch ? '2.5rem' : '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  minWidth: '250px',
                }}
              />
              {loadingSearch && (
                <div
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <svg
                    style={{
                      width: '16px',
                      height: '16px',
                      animation: 'spin 1s linear infinite',
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      style={{ opacity: 0.25 }}
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      style={{ opacity: 0.75 }}
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
              {showDropdown && searchResults.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  {searchResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectFilterItem(item)}
                      style={{
                        padding: '0.5rem',
                        cursor: 'pointer',
                        borderBottom:
                          index < searchResults.length - 1 ? '1px solid #e9ecef' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      {item.code ? (
                        <div>
                          <div style={{ fontWeight: '500' }}>{item.name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{item.code}</div>
                        </div>
                      ) : (
                        <div style={{ fontWeight: '500' }}>{item.name}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
          <button
            onClick={handleRun}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {loading ? (
              <>
                <svg
                  style={{
                    width: '16px',
                    height: '16px',
                    animation: 'spin 1s linear infinite',
                  }}
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Running...</span>
              </>
            ) : (
              <span>Run Report</span>
            )}
          </button>
          <button
            onClick={handleReset}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
          >
            Reset
          </button>
        </div>
      </div>
    </>
  );
};

export default React.memo(FilterBar);
